import React, { lazy, useEffect } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import DashboardLayout from './components/layout/DashboardLayout';
import Dashboard from './pages/Dashboard';
import WarehouseList from './pages/Warehouse/List';
import WarehouseDetail from './pages/Warehouse/Detail';
import WarehouseEdit from './pages/Warehouse/Edit';
import CreateProjectGroup from './pages/project/ProjectGroup/Create';
import ProjectGroupList from './pages/project/ProjectGroup/List';
import ProjectGroupDetail from './pages/project/ProjectGroup/Detail';
import CreateProject from './pages/project/project/CreateProject';
import { TabsProvider } from './contexts/TabsContext';
import ProjectList from './pages/project/ProjectList';
import ProjectAddress from './pages/project/project/ProjectAddress';
import ProductList from './pages/Product/List';
import CreateProduct from './pages/Product/Create';
import BatchCreateProduct from './pages/Product/BatchCreate';
import LogisticsProviderList from './pages/logistics/providers/List';
import ShippingPackagingListPage from './pages/logistics/shipping-packaging/List';
import PackageEstimatorPage from './pages/logistics/package-estimator/Index';
import ShippingRatesPage from './pages/logistics/shipping-rates/Index';
import CustomerAddressBookPage from './pages/logistics/address-book/List';
import ShipmentsListPage from './pages/logistics/shipments/List';
import LogisticsSystemPage from './pages/logistics/logistics-system/Index';
import { cleanupExpiredPageStates } from './utils/paginationStorage';
import { PermissionRoute } from './components/PermissionRoute';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';



// 管理员专属功能组件
import AdminUserList from './pages/admin/users/List';
import AdminUserCreate from './pages/admin/users/Create';
import AdminUsersRoles from './pages/admin/users/Roles';
import PermissionManagement from './pages/admin/PermissionManagement';
import SensitiveFieldsPage from './pages/admin/PermissionManagement/SensitiveFieldsPage';
import MenuManagement from './pages/admin/MenuManagement';
import AdminSystemBasicSettings from './pages/admin/system/BasicSettings';
import SystemBillingPage from './pages/admin/billing/SystemBillingPage';

// 新增的共用功能组件
import OutboundRecordsPage from './pages/outbound/Records';
import ServiceCenterPage from './pages/customer-service/ServiceCenter';
import ServiceTicketsPage from './pages/customer-service/Tickets';
import ServiceGuidePage from './pages/customer-service/Guide';
import ServiceFaqPage from './pages/customer-service/FAQ';
import ServiceToolsPage from './pages/customer-service/Tools';
import InboundScanPage from './pages/admin-staff/inbound/scan/Scan';
import PutAway from './pages/admin-staff/inbound/put-away/PutAway';
import AdminStaffCurrentInventory from './pages/admin-staff/inventory/CurrentInventory';
import AdminStaffInventoryLocation from './pages/admin-staff/inventory/Location';
// import AdminStaffInventoryHistory from './pages/admin-staff/inventory/History';

// 管理员和员工共用的独立组件（替换客户后台组件）
import AdminStaffProductList from './pages/admin-staff/products/List';
import AdminStaffProjectList from './pages/admin-staff/projects/List';
import AdminStaffInventoryConfigList from './pages/admin-staff/inventory-config/List';
import ImplementationStatus from './pages/admin-staff/test/ImplementationStatus';
import BillingRules from './pages/billing/BillingRules';
import AdminBillingRulesPage from './pages/admin/billing/BillingRules';
import ReturnInspectionRulesPage from './pages/return-inspection/Rules';
import AdminReturnInspectionRulesPage from './pages/admin/return-inspection/Rules';
import BillingRecords from './pages/billing/BillingRecords';
import BillsPage from './pages/billing/Bills';
import FundsPage from './pages/finance/Funds';
import { useTranslation } from 'react-i18next';
import AdminWarehouseCreate from './pages/admin/warehouse/Create';
import SubAccountManagement from './pages/customer/account/SubAccountManagement';
import AccountProfilePage from './pages/account/Profile';
import SubAccountEntry from './pages/admin/customers/SubAccountEntry';
import AdminCustomerCreate from './pages/admin/customers/Create';
import AdminCustomerList from './pages/admin/customers/List';
import AdminLogisticsCreate from './pages/admin/logistics/Create';
import InstructionSystemPage from './pages/admin/instruction/System';
import InstructionListPage from './pages/instruction/List';
import InstructionCreatePage from './pages/instruction/Create';
import InstructionBulkCreatePage from './pages/instruction/BulkCreate';
import InstructionImportCreatePage from './pages/instruction/ImportCreate';
import InstructionEditPage from './pages/instruction/Edit';
import InstructionDetailPage from './pages/instruction/Detail';
// 终审页入口：库存非标准操作指令走自己的终审页，其他指令仍进入原来的终审页
import InstructionFinalReviewPage from './pages/instruction/FinalReviewSwitch';
import InstructionBatchListPage from './pages/instruction/BatchList';
import DropshipBatchDetailPage from './pages/instruction/DropshipBatchDetail';
import AdminInstructionCreatePlaceholder from './pages/admin/instruction/AdminInstructionCreatePlaceholder';
import AdminRouteNotFound from './pages/admin/instruction/AdminRouteNotFound';
import LogSystemPage from './pages/admin/log-system/LogSystem';
import WorkOrderListPage from './pages/admin-staff/work-orders/List';
import AdminWorkOrderTemplateList from './pages/admin/work-orders/templates/List';
import AdminWorkOrdersIndex from 'pages/admin/work-orders/Index';
import AdminWorkOrderTemplateEdit from 'pages/admin/work-orders/templates/Edit';
import RatePreviewPage from './pages/admin/billing/RatePreview';
import BillingRulesPage from './pages/admin/billing/Rules';
import InstructionReconcilePage from './pages/admin/billing/InstructionReconcile';
import AdminInternalWOCreate from './pages/admin/internal-work-orders/Create';
import AdminInternalWOList from './pages/admin/internal-work-orders/List';
import StaffExecutionTodoPage from './pages/staff/execution/Todo';
import StaffInstructionExecutePage from './pages/staff/execution/Execute';
import BatchExecutePage from './pages/staff/execution/BatchExecute';
import SortAndCountWorkbench from './pages/staff/execution/SortAndCountWorkbench';
import MarketingHomePage from './pages/marketing/Home';
import FAQPage from './pages/marketing/FAQ';
import GuidePage from './pages/marketing/Guide';
import AnnouncementsListPage from './pages/marketing/AnnouncementsList';
import GenericContentListPage from './pages/marketing/GenericContentList';
import GenericContentDetailPage from './pages/marketing/GenericContentDetail';
import ResourceHubPage from './pages/marketing/resources/ResourceHub';
import ResourceChannelListPage from './pages/marketing/resources/ResourceChannelList';
import ResourceItemDetailPage from './pages/marketing/resources/ResourceItemDetail';
import ServiceDetailPage from './pages/marketing/ServiceDetail';
import AnnouncementDetailPage from './pages/marketing/AnnouncementDetail';
import ContactPage from './pages/marketing/Contact';
import AboutPage from './pages/marketing/About';
import RegisterPlaceholderPage from './pages/marketing/RegisterPlaceholder';
import RegisterVerifyPage from './pages/marketing/RegisterVerify';
import ForgotPasswordPage from './pages/marketing/ForgotPassword';
import ResetPasswordPage from './pages/marketing/ResetPassword';
import AdminContentListPage from './pages/admin/content/List';
import AdminContentEditPage from './pages/admin/content/Edit';
import AdminContentPreviewPage from './pages/admin/content/Preview';
import SelectBusinessModulePage from './pages/module/SelectBusinessModule';
import ModulePlaceholderPage from './pages/module/ModulePlaceholder';
import { ACTIVE_BUSINESS_MODULE_KEY, DEFAULT_MODULE_KEY } from './constants/businessModules';
import { resendPublicVerifyEmail } from './services/auth';

// 测试页面组件
const TabStateTest = lazy(() => import('./pages/admin-staff/test/TabStateTest'));

// 时区设置页面
const TimezoneSettingsPage = lazy(() => import('./pages/settings/TimezoneSettingsPage'));

// 偏好设定页面
const PreferencesPage = lazy(() => import('./pages/settings/PreferencesPage'));

const InventoryConfigList = lazy(() => import('./pages/project/inventory-config/List'));
const CreateInventoryConfig = lazy(() => import('./pages/project/inventory-config/Create'));
const EditInventoryConfig = lazy(() => import('./pages/project/inventory-config/Edit'));
const InventoryConfigDetail = lazy(() => import('./pages/project/inventory-config/Detail'));
const InventoryUnitLabelList = lazy(() => import('./pages/project/inventory-config/labels/List'));
const InboundForecastList = lazy(() => import('./pages/inbound/inbound-forecast/List'));
const InboundForecastCreate = lazy(() => import('./pages/inbound/inbound-forecast/Create'));
const InboundForecastBulkCreate = lazy(() => import('./pages/inbound/inbound-forecast/BulkCreate'));
const InboundForecastDetail = lazy(() => import('./pages/inbound/inbound-forecast/Detail'));
const InboundForecastLabel = lazy(() => import('./pages/inbound/inbound-forecast/labels/components/Label'));

// 入库记录组件
const InboundRecordList = lazy(() => import('./pages/inbound/inbound-records/List'));
const InboundRecordDetail = lazy(() => import('./pages/inbound/inbound-records/Detail'));

// 库存管理组件
const CurrentInventory = lazy(() => import('./pages/inventory/CurrentInventory'));
const InventoryDetail = lazy(() => import('./pages/inventory/InventoryDetail').then(module => ({ default: module.default })));
const InventoryHistory = lazy(() => import('./pages/inventory/InventoryHistory'));

// 登录页面组件
const LoginPage: React.FC = () => {
  const { login, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const loginRedirectDoneRef = React.useRef(false);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [resendEmail, setResendEmail] = React.useState('');
  const [resendLoading, setResendLoading] = React.useState(false);
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [resendTip, setResendTip] = React.useState('');
  const [currentLang, setCurrentLang] = React.useState(localStorage.getItem('lang') || 'zh-CN');
  const { t, i18n } = useTranslation();
  const accountLabel = currentLang === 'en-US' ? 'Account (username or email)' : '账号（用户名或邮箱）';
  const isUnverifiedError = error.includes('邮箱未验证');

  // 初始化时同步语言
  React.useEffect(() => {
    if (i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  // 调试信息

  const handleChangeLang = (lang: string) => {
    setCurrentLang(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('lang', lang);
  };

  const getTargetPathAfterLogin = (loggedInUser: any) => {
    const role = loggedInUser?.role;
    if (role === 'customer' && loggedInUser?.customer_sub_role === 'sub_account') {
      localStorage.setItem(ACTIVE_BUSINESS_MODULE_KEY, DEFAULT_MODULE_KEY);
      return '/dashboard/customer';
    }
    return '/dashboard/select-module';
  };

  React.useEffect(() => {
    if (authLoading || !user) return;
    if (loginRedirectDoneRef.current) return;
    const targetPath = getTargetPathAfterLogin(user);
    navigate(targetPath, { replace: true });
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResendTip('');
    setIsLoading(true);

    try {
      const loggedInUser = await login(username, password);
      const targetPath = getTargetPathAfterLogin(loggedInUser);
      loginRedirectDoneRef.current = true;
      navigate(targetPath, { replace: true });
    } catch (error: any) {
      const msg = error.response?.data?.error || t('loginFailed');
      setError(msg);
      if (String(msg).includes('邮箱未验证') && String(username).includes('@')) {
        setResendEmail(String(username).trim().toLowerCase());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerifyEmail = async () => {
    const normalizedEmail = String(resendEmail || '').trim().toLowerCase();
    if (!normalizedEmail) {
      setResendTip('请输入邮箱后再重发验证邮件');
      return;
    }
    setResendLoading(true);
    setResendTip('');
    try {
      const result = await resendPublicVerifyEmail(normalizedEmail);
      setResendTip(result?.detail || '验证邮件已发送，请查收邮箱');
      setResendCooldown(60);
    } catch (err: any) {
      setResendTip(err?.response?.data?.detail || '重发失败，请稍后重试');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-[500px]">
        {/* 顶部操作区 */}
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="rounded border border-blue-300 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
          >
            返回主页
          </Link>
          <div className="flex items-center space-x-2">
            <button
              className={`px-2 py-1 text-xs rounded ${
                currentLang === 'zh-CN' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => handleChangeLang('zh-CN')}
            >
              中文
            </button>
            <button
              className={`px-2 py-1 text-xs rounded ${
                currentLang === 'en-US' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => handleChangeLang('en-US')}
            >
              English
            </button>
          </div>
        </div>

        <h1 className="mt-8 text-2xl font-bold mb-6 text-center">{t('login')}</h1>
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        {isUnverifiedError ? (
          <div className="mb-4 rounded border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-800">该账号邮箱尚未验证，请先完成邮箱验证后再登录。</p>
            <div className="mt-3 flex gap-2">
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="输入注册邮箱"
                className="flex-1 rounded border border-amber-200 px-3 py-2 text-sm outline-none focus:border-amber-400"
                disabled={resendLoading}
              />
              <button
                type="button"
                onClick={handleResendVerifyEmail}
                disabled={resendLoading || resendCooldown > 0}
                className="rounded border border-amber-300 px-3 py-2 text-sm text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resendLoading ? '发送中...' : resendCooldown > 0 ? `重发(${resendCooldown}s)` : '重发验证邮件'}
              </button>
            </div>
            {resendTip ? <p className="mt-2 text-xs text-amber-700">{resendTip}</p> : null}
          </div>
        ) : null}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="username">
              {accountLabel}
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
              {t('password')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pr-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 p-1 rounded focus:outline-none"
                aria-label={showPassword ? '隐藏密码' : '显示密码'}
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? t('loggingIn') : t('login')}
          </button>
        </form>

        <div className="mt-5 text-center text-sm text-gray-600">
          {currentLang === 'en-US' ? "Don't have an account? " : '还没有注册账号？'}
          <Link to="/register" className="text-blue-600 hover:text-blue-700">
            {currentLang === 'en-US' ? 'Register' : '去注册'}
          </Link>
        </div>
        <div className="mt-2 text-center text-sm">
          <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700">
            {currentLang === 'en-US' ? 'Forgot password?' : '忘记密码？'}
          </Link>
        </div>
      </div>
    </div>
  );
};

// （移除通用首页，不做兜底重定向）

// 管理员 Dashboard 首页组件
const AdminDashboardHome: React.FC = () => {
  return <Dashboard />;
};

// 员工 Dashboard 首页组件
const StaffDashboardHome: React.FC = () => {
  return <Dashboard />;
};

// 受保护的路由组件
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>加载中...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// 管理员角色验证组件
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const { isAdmin } = usePermissions();

  if (isLoading) {
    return <div>加载中...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 使用权限系统验证管理员权限
  if (!isAdmin()) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// 员工角色验证组件
const StaffRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const { isHQStaff, isWarehouseSupervisor, isWarehouseStaff } = usePermissions();

  if (isLoading) {
    return <div>加载中...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 使用权限系统验证员工权限
  if (!isHQStaff() && !isWarehouseSupervisor() && !isWarehouseStaff()) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Dashboard 布局组件
const DashboardWrapper: React.FC = () => {
  return (
    <AuthProvider>
      <PermissionProvider>
        <TabsProvider>
          <ProtectedRoute>
            <DashboardLayout>
              <Outlet />
            </DashboardLayout>
          </ProtectedRoute>
        </TabsProvider>
      </PermissionProvider>
    </AuthProvider>
  );
};

// 管理员 Dashboard 布局组件
const AdminDashboardWrapper: React.FC = () => {
  // 与客户/员工统一：TabsProvider 只在客户容器下提供，管理员容器复用同一 Tabs 容器
  return (
    <AuthProvider>
      <PermissionProvider>
        <AdminRoute>
          <TabsProvider>
            <DashboardLayout>
              {(() => {
                try {
                  const { pathname, search } = (window as any).location || { pathname: '', search: '' };
                  console.debug('[AdminDashboardWrapper] location=', pathname, search);
                } catch {}
                return null;
              })()}
              <Outlet />
            </DashboardLayout>
          </TabsProvider>
        </AdminRoute>
      </PermissionProvider>
    </AuthProvider>
  );
};

// 员工 Dashboard 布局组件
const StaffDashboardWrapper: React.FC = () => {
  // 员工容器补齐 TabsProvider，确保使用 useTabs 的页面（如批次详情）可正常渲染
  return (
    <AuthProvider>
      <PermissionProvider>
        <StaffRoute>
          <TabsProvider>
            <DashboardLayout>
              <Outlet />
            </DashboardLayout>
          </TabsProvider>
        </StaffRoute>
      </PermissionProvider>
    </AuthProvider>
  );
};

// 创建路由配置
const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <MarketingHomePage />,
    },
    {
      path: '/services',
      element: <Navigate to="/service/warehouse-3pl" replace />,
    },
    {
      path: '/pricing',
      element: <Navigate to="/service/warehouse-3pl" replace />,
    },
    {
      path: '/service/:slug',
      element: <ServiceDetailPage />,
    },
    {
      path: '/faq',
      element: <FAQPage />,
    },
    {
      path: '/guide',
      element: <GuidePage />,
    },
    {
      path: '/announcements',
      element: <AnnouncementsListPage />,
    },
    {
      path: '/announcements/:id',
      element: <AnnouncementDetailPage />,
    },
    {
      path: '/content/:slug',
      element: <GenericContentDetailPage />,
    },
    {
      path: '/content',
      element: <GenericContentListPage />,
    },
    {
      path: '/resources',
      element: <ResourceHubPage />,
    },
    {
      path: '/resources/:channelSlug',
      element: <ResourceChannelListPage />,
    },
    {
      path: '/resources/:channelSlug/:slug',
      element: <ResourceItemDetailPage />,
    },
    {
      path: '/contact',
      element: <ContactPage />,
    },
    {
      path: '/about',
      element: <AboutPage />,
    },
    {
      path: '/register',
      element: <RegisterPlaceholderPage />,
    },
    {
      path: '/register/verify',
      element: <RegisterVerifyPage />,
    },
    {
      path: '/forgot-password',
      element: <ForgotPasswordPage />,
    },
    {
      path: '/reset-password',
      element: <ResetPasswordPage />,
    },
    {
      path: '/login',
      element: (
        <AuthProvider>
          <PermissionProvider>
            <LoginPage />
          </PermissionProvider>
        </AuthProvider>
      ),
    },
    {
      path: '/dashboard/select-module',
      element: (
        <AuthProvider>
          <PermissionProvider>
            <ProtectedRoute>
              <SelectBusinessModulePage />
            </ProtectedRoute>
          </PermissionProvider>
        </AuthProvider>
      ),
    },
    {
      path: '/dashboard/module/:moduleKey',
      element: (
        <AuthProvider>
          <PermissionProvider>
            <ProtectedRoute>
              <ModulePlaceholderPage />
            </ProtectedRoute>
          </PermissionProvider>
        </AuthProvider>
      ),
    },
    {
      path: '/dashboard',
      element: <DashboardWrapper />,
      children: [
        // 移除索引路由，避免进入通用首页
        {
          path: 'customer',
          element: <Dashboard />, // 客户工作台
        },
        {
          path: 'customer/account/profile',
          element: <AccountProfilePage />,
        },
        {
          path: 'staff/account/profile',
          element: <AccountProfilePage />,
        },
        {
          path: 'admin/account/profile',
          element: <AccountProfilePage />,
        },
        {
          path: 'customer/warehouse/available',
          element: <WarehouseList />,
        },
        {
          path: 'warehouses/list',
          element: <WarehouseList />,
        },
        {
          path: 'warehouses/:id',
          element: <WarehouseDetail />,
        },
        {
          path: 'customer/warehouses/:id',
          element: <WarehouseDetail />,
        },
        {
          path: 'customer/logistics/shipping-carriers',
          element: <LogisticsProviderList />,
        },
        {
          path: 'customer/logistics/package-estimator',
          element: <PackageEstimatorPage />,
        },
        {
          path: 'customer/logistics/shipping-rates',
          element: <ShippingRatesPage />,
        },
        {
          path: 'customer/logistics/address-book',
          element: <CustomerAddressBookPage />,
        },
        {
          path: 'customer/logistics/shipments',
          element: <ShipmentsListPage />,
        },
        {
          path: 'customer/project-groups/create',
          element: (
            <PermissionRoute requiredRoles={['main_account']} fallbackPath="/dashboard/customer">
              <CreateProjectGroup />
            </PermissionRoute>
          ),
        },
        {
          path: 'customer/project-groups/list',
          element: (
            <PermissionRoute requiredRoles={['main_account']} fallbackPath="/dashboard/customer">
              <ProjectGroupList />
            </PermissionRoute>
          ),
        },
        {
          path: 'customer/project-groups/:id',
          element: (
            <PermissionRoute requiredRoles={['main_account']} fallbackPath="/dashboard/customer">
              <ProjectGroupDetail />
            </PermissionRoute>
          ),
        },
        {
          path: 'customer/projects/create',
          element: (
            <PermissionRoute requiredPermission="project:create" fallbackPath="/dashboard/customer" showFallbackPage={false} showNotFoundPage>
              <CreateProject />
            </PermissionRoute>
          ),
        },
        {
          path: 'customer/projects/list',
          element: <ProjectList />,
        },
        {
          path: 'projects/:id/address',
          element: <ProjectAddress />,
        },
        // 管理员路径明确
        {
          path: 'admin/projects/:id/address',
          element: <ProjectAddress />,
        },
        // 客户路径别名，兼容 /dashboard/customer/projects/:id/address
        {
          path: 'customer/projects/:id/address',
          element: <ProjectAddress />,
        },
        {
          path: 'customer/projects/basic-config',
          element: <InventoryConfigList />,
        },
        {
          path: 'projects/inventory-config/create',
          element: <CreateInventoryConfig />,
        },
        // 管理员与员工也已有各自 create 路由，客户路径上方已添加
        {
          path: 'projects/inventory-config/:id',
          element: <InventoryConfigDetail />,
        },
        {
          path: 'projects/inventory-config/:id/edit',
          element: <EditInventoryConfig />,
        },
        {
          path: 'projects/inventory-config/:id/labels',
          element: <InventoryUnitLabelList />,
        },
        // 系统配置 - 基础设置（管理员路径示例放在全局容器下不合适，放在管理员容器下）
        // 客户路径别名，保持与菜单一致
        {
          path: 'customer/projects/inventory-config/create',
          element: <CreateInventoryConfig />,
        },
        {
          path: 'customer/projects/inventory-config/:id',
          element: <InventoryConfigDetail />,
        },
        {
          path: 'customer/projects/inventory-config/:id/edit',
          element: <EditInventoryConfig />,
        },
        {
          path: 'customer/projects/inventory-config/:id/labels',
          element: <InventoryUnitLabelList />,
        },
        {
          path: 'customer/products/create',
          element: <CreateProduct />,
        },
        {
          path: 'customer/products/list',
          element: <ProductList />,
        },
        {
          path: 'customer/products/:id/edit',
          element: <CreateProduct />,
        },
        {
          path: 'customer/products/batch-create',
          element: <BatchCreateProduct />,
        },
        {
          path: 'customer/inbound/forecast/list',
          element: <InboundForecastList />,
        },
        {
          path: 'inbound/forecast/create',
          element: <InboundForecastCreate />,
        },
        {
          path: 'inbound/forecast/bulk-create',
          element: <InboundForecastBulkCreate />,
        },
        {
          path: 'customer/inbound/forecast/:id',
          element: <InboundForecastDetail />, 
        },
        {
          path: 'customer/inbound/forecast/:id/labels',
          element: <InboundForecastLabel />, 
        },
        // 入库记录
        {
          path: 'customer/inbound/records',
          element: <InboundRecordList />,
        },
        {
          path: 'customer/inbound/records/:id',
          element: <InboundRecordDetail />,
        },
        // 库存管理
        {
          path: 'customer/inventory/current',
          element: <CurrentInventory />,
        },
        {
          path: 'customer/inventory/detail/:id',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <InventoryDetail />
            </React.Suspense>
          ),
        },
        {
          path: 'customer/inventory/history',
          element: <InventoryHistory />,
        },
        // 客户指令系统
        {
          path: 'customer/instructions/list',
          element: <InstructionListPage />,
        },
        {
          path: 'customer/instructions/create',
          element: <InstructionCreatePage />,
        },
        {
          path: 'customer/instructions/bulk-create',
          element: <InstructionBulkCreatePage />,
        },
        {
          path: 'customer/instructions/import',
          element: <InstructionImportCreatePage />,
        },
        {
          path: 'customer/instructions/:id/edit',
          element: <InstructionEditPage />,
        },
        {
          path: 'customer/instructions/:id',
          element: <InstructionDetailPage />,
        },
        // 客户财务 - 收费规则展示
        {
          path: 'customer/finance/billing-rules',
          element: (
            <PermissionRoute requiredRoles={['main_account']} fallbackPath="/dashboard/customer">
              <BillingRules />
            </PermissionRoute>
          ),
        },
        // 管理员财务 - 收费规则（复用客户页面 + 管理员客户筛选）
        {
          path: 'admin/finance/billing-rules',
          element: <AdminBillingRulesPage />,
        },
        // 项目管理 - 退货初检规则（客户主账号 + 子账号共用；数据范围由后端按项目可见范围限定，
        // 子账号只能管理分配给自己的项目，所以这里不再限定 main_account）
        {
          path: 'customer/projects/return-inspection-rules',
          element: <ReturnInspectionRulesPage />,
        },
        // 客户财务 - 收费记录
        {
          path: 'customer/finance/billing-records',
          element: (
            <PermissionRoute requiredRoles={['main_account']} fallbackPath="/dashboard/customer">
              <BillingRecords />
            </PermissionRoute>
          ),
        },
        // 客户财务 - 账单
        {
          path: 'customer/finance/bills',
          element: (
            <PermissionRoute requiredRoles={['main_account']} fallbackPath="/dashboard/customer">
              <BillsPage />
            </PermissionRoute>
          ),
        },
        // 客户财务 - 资金账户
        {
          path: 'customer/finance/funds',
          element: (
            <PermissionRoute requiredRoles={['main_account']} fallbackPath="/dashboard/customer">
              <FundsPage />
            </PermissionRoute>
          ),
        },
        // 出库管理
        {
          path: 'customer/outbound/records',
          element: <OutboundRecordsPage />,
        },
        {
          path: 'customer/customer-service/service-center',
          element: <ServiceCenterPage />,
        },
        {
          path: 'customer/customer-service/tickets',
          element: <ServiceTicketsPage />,
        },
        {
          path: 'customer/customer-service/guide',
          element: <ServiceGuidePage />,
        },
        {
          path: 'customer/customer-service/FAQ',
          element: <ServiceFaqPage />,
        },
        {
          path: 'customer/customer-service/tools',
          element: <ServiceToolsPage />,
        },
        
        // 指令管理
        {
          path: 'instructions/list',
          element: <InstructionListPage />, 
        },
        {
          // 与列表/详情页的 goto(`/instructions/create?...`) 对齐：/dashboard/{admin|staff}/instructions/create
          path: 'instructions/create',
          element: <InstructionCreatePage />,
        },
        {
          path: 'instructions/bulk-create',
          element: <InstructionBulkCreatePage />,
        },
        {
          path: 'instructions/import',
          element: <InstructionImportCreatePage />,
        },
        {
          path: 'instructions/batch',
          element: (
            <PermissionRoute requiredRoles={['admin']} fallbackPath="/dashboard/admin">
              <InstructionBatchListPage />
            </PermissionRoute>
          ),
        },
        {
          path: 'instructions/batch/:id',
          element: (
            <PermissionRoute requiredRoles={['admin']} fallbackPath="/dashboard/admin">
              <DropshipBatchDetailPage />
            </PermissionRoute>
          ),
        },
        {
          path: 'admin/instructions/batch',
          element: (
            <PermissionRoute requiredRoles={['admin']} fallbackPath="/dashboard/admin">
              <InstructionBatchListPage />
            </PermissionRoute>
          ),
        },
        {
          path: 'admin/instructions/batch/:id',
          element: (
            <PermissionRoute requiredRoles={['admin']} fallbackPath="/dashboard/admin">
              <DropshipBatchDetailPage />
            </PermissionRoute>
          ),
        },
        {
          path: 'admin/instructions/bulk-create',
          element: <InstructionBulkCreatePage />,
        },
        {
          path: 'admin/instructions/import',
          element: <InstructionImportCreatePage />,
        },
        {
          path: 'instructions/:id',
          element: <InstructionDetailPage />,
        },
        {
          path: 'instructions/:id/edit',
          element: <InstructionEditPage />,
        },
        {
          // 兼容 staff 旧/显式路径：/dashboard/staff/instructions/create
          path: 'staff/instructions/create',
          element: <InstructionCreatePage />,
        },
        {
          path: 'admin/instructions/create',
          element: <InstructionCreatePage />,
        },
        {
          // 终审工作台（正确路径）：/dashboard/admin/instructions/:id/final-review
          path: 'instructions/:id/final-review',
          element: (
            <PermissionRoute requiredRoles={['admin']} fallbackPath="/dashboard/admin">
              <InstructionFinalReviewPage />
            </PermissionRoute>
          ),
        },
        {
          // 兼容旧URL（避免已有标签页 404）：/dashboard/admin/admin/instructions/:id/final-review
          path: 'admin/instructions/:id/final-review',
          element: (
            <PermissionRoute requiredRoles={['admin']} fallbackPath="/dashboard/admin">
              <InstructionFinalReviewPage />
            </PermissionRoute>
          ),
        },
        // 调试：管理员未匹配路由
        {
          path: '*',
          element: <AdminRouteNotFound />,
        },
        // 时区设置
        {
          path: 'settings/timezone',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <TimezoneSettingsPage />
            </React.Suspense>
          ),
        },
        // 偏好设定
        {
          path: 'account/preferences',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <PreferencesPage />
            </React.Suspense>
          ),
        },
        // 偏好设定（客户专用路径以兼容菜单配置）
        {
          path: 'customer/account/preferences',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <PreferencesPage />
            </React.Suspense>
          ),
        },
        // 客户主账号 - 子账号管理
        {
          path: 'customer/account/sub-account',
          element: (
            <PermissionRoute requiredRoles={['main_account']} fallbackPath="/dashboard/customer">
              <React.Suspense fallback={<div>加载中...</div>}>
                {React.createElement(require('./pages/customer/account/SubAccountManagement').default)}
              </React.Suspense>
            </PermissionRoute>
          ),
        },
        // 测试页面
        {
          path: 'test/implementation-status',
          element: <ImplementationStatus />,
        },
        {
          path: 'test/tab-state',
          element: <TabStateTest />,
        },
      ],
    },
    {
      path: '/dashboard/admin',
      element: <AdminDashboardWrapper />,
      children: [
        {
          path: '',
          element: <AdminDashboardHome />,
        },
        {
          path: 'log-system',
          element: <LogSystemPage />,
        },
        {
          path: 'log-system/config',
          element: <LogSystemPage />,
        },
        {
          path: 'instruction-system',
          element: <InstructionSystemPage />,
        },
        {
          path: 'warehouse/create',
          element: <AdminWarehouseCreate />, // 创建仓库（管理员）
        },
        {
          path: 'warehouse/available',
          element: <WarehouseList />,
        },
        {
          path: 'warehouses/list',
          element: <WarehouseList />,
        },
        {
          path: 'warehouses/:id',
          element: <WarehouseDetail />,
        },
        {
          path: 'warehouses/:id/edit',
          element: <WarehouseEdit />,
        },
        {
          path: 'logistics/create',
          element: <AdminLogisticsCreate />,
        },
        {
          path: 'logistics/shipping-carriers',
          element: <LogisticsProviderList />,
        },
        {
          path: 'logistics/package-estimator',
          element: <PackageEstimatorPage />,
        },
        {
          path: 'logistics/shipping-rates',
          element: <ShippingRatesPage />,
        },
        {
          path: 'logistics/shipping-package',
          element: <ShippingPackagingListPage />,
        },
        {
          path: 'logistics/address-book',
          element: <CustomerAddressBookPage />,
        },
        {
          path: 'logistics/shipments',
          element: <ShipmentsListPage />,
        },
        {
          path: 'project-groups/create',
          element: <CreateProjectGroup />,
        },
        {
          path: 'project-groups/list',
          element: <ProjectGroupList />,
        },
        {
          path: 'project-groups/:id',
          element: <ProjectGroupDetail />,
        },
        {
          path: 'projects/create',
          element: <CreateProject />, 
        },
        {
          path: 'projects/list',
          element: <AdminStaffProjectList />,
        },
        {
          path: 'projects/:id/address',
          element: <ProjectAddress />,
        },
        {
          path: 'projects/basic-config',
          element: <AdminStaffInventoryConfigList />,
        },
        {
          path: 'projects/inventory-config/create',
          element: <CreateInventoryConfig />,
        },
        {
          path: 'projects/inventory-config/:id',
          element: <InventoryConfigDetail />,
        },
        {
          path: 'projects/inventory-config/:id/edit',
          element: <EditInventoryConfig />,
        },
        {
          path: 'projects/inventory-config/:id/labels',
          element: <InventoryUnitLabelList />,
        },
        // 项目管理 - 退货初检规则（管理员：先选客户，再管理其项目/库存单元的规则）
        {
          path: 'projects/return-inspection-rules',
          element: <AdminReturnInspectionRulesPage />,
        },
        {
          path: 'products/create',
          element: <CreateProduct />,
        },
        {
          path: 'products/list',
          element: <AdminStaffProductList />,
        },
        {
          path: 'products/:id/edit',
          element: <CreateProduct />,
        },
        {
          path: 'products/batch-create',
          element: <BatchCreateProduct />,
        },
        {
          path: 'inbound/forecast/list',
          element: <InboundForecastList />,
        },
        {
          path: 'inbound/forecast/create',
          element: <InboundForecastCreate />,
        },
        {
          path: 'inbound/forecast/bulk-create',
          element: <InboundForecastBulkCreate />,
        },
        {
          path: 'inbound/forecast/:id',
          element: <InboundForecastDetail />,
        },
        {
          path: 'inbound/forecast/:id/labels',
          element: <InboundForecastLabel />,
        },
        // 入库记录
        {
          path: 'inbound/records',
          element: <InboundRecordList />,
        },
        {
          path: 'inbound/records/:id',
          element: <InboundRecordDetail />,
        },
        // 扫描入库
        {
          path: 'inbound/scan',
          element: <InboundScanPage />,
        },
        // 上架管理
        {
          path: 'inbound/put-away',
          element: <PutAway />,
        },
        // 库存管理
        {
          path: 'inventory/current',
          element: <AdminStaffCurrentInventory />,
        },
        {
          path: 'inventory/detail/:id',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <InventoryDetail />
            </React.Suspense>
          ),
        },
        {
          path: 'inventory/history',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <InventoryHistory />
            </React.Suspense>
          ), 
        },
        {
          path: 'inventory/location',
          element: <AdminStaffInventoryLocation />,
        },
        // 出库管理
        {
          path: 'outbound/records',
          element: <OutboundRecordsPage />,
        },
        {
          path: 'customer-service/service-center',
          element: <ServiceCenterPage />,
        },
        {
          path: 'customer-service/tickets',
          element: <ServiceTicketsPage />,
        },
        {
          path: 'customer-service/guide',
          element: <ServiceGuidePage />,
        },
        {
          path: 'customer-service/FAQ',
          element: <ServiceFaqPage />,
        },
        {
          path: 'customer-service/tools',
          element: <ServiceToolsPage />,
        },
        // 工单（管理员后台）
        {
          path: 'work-orders/list',
          element: <WorkOrderListPage />,
        },
        // 仓内工单（管理员后台）
        {
          path: 'internal-work-orders/create',
          element: <AdminInternalWOCreate />,
        },
        {
          path: 'internal-work-orders/list',
          element: <AdminInternalWOList />,
        },
        // 指令管理
        {
          path: 'instructions/list',
          element: <InstructionListPage />, 
        },
        {
          path: 'instructions/import',
          element: <InstructionImportCreatePage />,
        },
        {
          path: 'instructions/:id',
          element: <InstructionDetailPage />,
        },
        {
          path: 'instructions/:id/edit',
          element: <InstructionEditPage />,
        },
        // 系统用户管理
        {
          path: 'users/list',
          element: <AdminUserList />,
        },
        // 系统配置 - 基础设置
        {
          path: 'system-settings',
          element: <AdminSystemBasicSettings />,
        },
        {
          path: 'users/create',
          element: <AdminUserCreate />,
        },
        {
          path: 'users/roles',
          element: <AdminUsersRoles />,
        },
        // 权限管理
        {
          path: 'permission-management',
          element: <PermissionManagement />,
        },
        // 管理员：工单系统聚合页（模板/路由）
        {
          path: 'work-orders',
          element: <AdminWorkOrdersIndex />,
        },
        // 兼容旧直达子页路径
        {
          path: 'work-orders/templates',
          element: <AdminWorkOrdersIndex />,
        },
        {
          path: 'work-orders/routes',
          element: <AdminWorkOrdersIndex />,
        },
        {
          path: 'work-orders/templates/create',
          element: <AdminWorkOrderTemplateEdit />,
        },
        {
          path: 'work-orders/templates/:id/edit',
          element: <AdminWorkOrderTemplateEdit />,
        },
        // 管理员后台：执行待办（与员工页复用组件，只是路由容器不同）
        {
          path: 'execution',
          element: <StaffExecutionTodoPage />,
        },
        {
          path: 'execution/instruction/:id',
          element: <StaffInstructionExecutePage />,
        },
        {
          path: 'execution/batch/:id',
          element: <BatchExecutePage />,
        },
        {
          path: 'execution/sort-and-count',
          element: <SortAndCountWorkbench />,
        },
        {
          path: 'quick-tools/sort-and-count',
          element: <SortAndCountWorkbench />,
        },
        // 收费系统入口
        {
          path: 'billing-system',
          element: <SystemBillingPage />,
        },
        // 物流系统入口：网关管理 + 承运商与物流产品 + 网关物流产品管理，仅管理员可见
        {
          path: 'logistics-system',
          element: (
            <PermissionRoute requiredRoles={['admin']} fallbackPath="/dashboard/admin">
              <LogisticsSystemPage />
            </PermissionRoute>
          ),
        },
        {
          path: 'billing/rate-preview',
          element: <RatePreviewPage />,
        },
        {
          path: 'billing/rules',
          element: <BillingRulesPage />,
        },
        {
          path: 'billing/instruction-reconcile',
          element: <InstructionReconcilePage />,
        },
        // 管理员 - 财务 - 收费记录
        {
          path: 'finance/billing-records',
          element: <BillingRecords />,
        },
        // 管理员 - 财务 - 账单
        {
          path: 'finance/bills',
          element: <BillsPage />,
        },
        // 管理员 - 财务 - 资金账户
        {
          path: 'finance/funds',
          element: <FundsPage />,
        },
        {
          path: 'permission-management/sensitive-fields',
          element: <SensitiveFieldsPage />,
        },
        // 子账号管理入口（管理员选择客户后再进入）
        {
          path: 'customers/sub-accounts',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <SubAccountEntry />
            </React.Suspense>
          ),
        },
        // 创建客户
        {
          path: 'customers/create',
          element: <AdminCustomerCreate />,
        },
        // 客户列表
        {
          path: 'customers/list',
          element: <AdminCustomerList />,
        },
        // 管理员复用客户子账号管理组件（通过 ?customer_id=XX 指定主账号）
        {
          path: 'customers/:customerId/sub-accounts',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <SubAccountManagement />
            </React.Suspense>
          ),
        },
        // 菜单管理
        {
          path: 'menu-management',
          element: <MenuManagement />,
        },
        {
          path: 'content/list',
          element: <AdminContentListPage />,
        },
        {
          path: 'content/edit/:id',
          element: <AdminContentEditPage />,
        },
        {
          path: 'content/preview/:id',
          element: <AdminContentPreviewPage />,
        },
        // 时区设置
        {
          path: 'settings/timezone',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <TimezoneSettingsPage />
            </React.Suspense>
          ),
        },
        // 偏好设定
        {
          path: 'account/preferences',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <PreferencesPage />
            </React.Suspense>
          ),
        },
        // 测试页面
        {
          path: 'test/implementation-status',
          element: <ImplementationStatus />,
        },
        {
          path: 'test/tab-state',
          element: <TabStateTest />,
        },
      ],
    },
    {
      path: '/dashboard/staff',
      element: <StaffDashboardWrapper />,
      children: [
        {
          path: '',
          element: <StaffDashboardHome />,
        },
        {
          path: 'warehouse/available',
          element: <WarehouseList />,
        },
        {
          path: 'warehouses/list',
          element: <WarehouseList />,
        },
        {
          path: 'warehouses/:id',
          element: <WarehouseDetail />,
        },
        {
          path: 'logistics/shipping-carriers',
          element: <LogisticsProviderList />,
        },
        {
          path: 'logistics/package-estimator',
          element: <PackageEstimatorPage />,
        },
        {
          path: 'logistics/shipping-rates',
          element: <ShippingRatesPage />,
        },
        {
          path: 'logistics/shipments',
          element: <ShipmentsListPage />,
        },
        {
          path: 'products/list',
          element: <AdminStaffProductList />,
        },
        {
          path: 'products/batch-create',
          element: <BatchCreateProduct />,
        },
        // 员工后台不提供创建产品路由；编辑产品与管理员一致
        {
          path: 'products/:id/edit',
          element: <CreateProduct />,
        },
        {
          path: 'project-groups/list',
          element: <ProjectGroupList />,
        },
        {
          path: 'project-groups/:id',
          element: <ProjectGroupDetail />,
        },
        {
          path: 'projects/list',
          element: <AdminStaffProjectList />,
        },
        {
          path: 'projects/create',
          element: <CreateProject />,
        },
        {
          path: 'projects/:id/address',
          element: <ProjectAddress />,
        },
        {
          path: 'projects/inventory-config',
          element: <AdminStaffInventoryConfigList />,
        },
        {
          path: 'projects/inventory-config/create',
          element: <CreateInventoryConfig />,
        },
        {
          path: 'projects/inventory-config/:id',
          element: <InventoryConfigDetail />,
        },
        {
          path: 'projects/inventory-config/:id/edit',
          element: <EditInventoryConfig />,
        },
        {
          path: 'projects/inventory-config/:id/labels',
          element: <InventoryUnitLabelList />,
        },
        {
          path: 'inbound/forecast/list',
          element: <InboundForecastList />,
        },
        {
          path: 'inbound/forecast/create',
          element: <InboundForecastCreate />,
        },
        {
          path: 'inbound/forecast/bulk-create',
          element: <InboundForecastBulkCreate />,
        },
        {
          path: 'inbound/forecast/:id',
          element: <InboundForecastDetail />,
        },
        {
          path: 'inbound/forecast/:id/labels',
          element: <InboundForecastLabel />,
        },
        // 入库记录
        {
          path: 'inbound/records',
          element: <InboundRecordList />,
        },
        {
          path: 'inbound/records/:id',
          element: <InboundRecordDetail />,
        },
        // 扫描入库
        {
          path: 'inbound/scan',
          element: <InboundScanPage />,
        },
        // 上架管理
        {
          path: 'inbound/put-away',
          element: <PutAway />,
        },
        // 库存管理
        {
          path: 'inventory/current',
          element: <AdminStaffCurrentInventory />,
        },
        {
          path: 'inventory/detail/:id',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <InventoryDetail />
            </React.Suspense>
          ),
        },
        {
          path: 'inventory/history',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <InventoryHistory />
            </React.Suspense>
          ),
        },
        {
          path: 'inventory/location',
          element: <AdminStaffInventoryLocation />,
        },
        // 出库管理
        {
          path: 'outbound/records',
          element: <OutboundRecordsPage />,
        },
        {
          path: 'customer-service/service-center',
          element: <ServiceCenterPage />,
        },
        {
          path: 'customer-service/tickets',
          element: <ServiceTicketsPage />,
        },
        {
          path: 'customer-service/guide',
          element: <ServiceGuidePage />,
        },
        {
          path: 'customer-service/FAQ',
          element: <ServiceFaqPage />,
        },
        {
          path: 'customer-service/tools',
          element: <ServiceToolsPage />,
        },
        // 工单（员工后台）
        {
          path: 'work-orders/list',
          element: <WorkOrderListPage />,
        },
        // 员工执行（待办指令/指令执行）
        {
          path: 'execution',
          element: <StaffExecutionTodoPage />,
        },
        {
          path: 'execution/instruction/:id',
          element: <StaffInstructionExecutePage />,
        },
        {
          path: 'execution/batch/:id',
          element: <BatchExecutePage />,
        },
        {
          path: 'execution/sort-and-count',
          element: <SortAndCountWorkbench />,
        },
        {
          path: 'quick-tools/sort-and-count',
          element: <SortAndCountWorkbench />,
        },
        // 仓内工单（员工后台）
        {
          path: 'internal-work-orders/list',
          element: <AdminInternalWOList />,
        },
        // 指令管理
        {
          path: 'instructions/list',
          element: <InstructionListPage />,
        },
        {
          path: 'instructions/import',
          element: <InstructionImportCreatePage />,
        },
        // 员工查看批次详情（只读入口，复用批次详情页）
        {
          path: 'instructions/batch/:id',
          element: <DropshipBatchDetailPage />,
        },
        // 时区设置
        {
          path: 'settings/timezone',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <TimezoneSettingsPage />
            </React.Suspense>
          ),
        },
        // 偏好设定
        {
          path: 'account/preferences',
          element: (
            <React.Suspense fallback={<div>加载中...</div>}>
              <PreferencesPage />
            </React.Suspense>
          ),
        },
        // 测试页面
        {
          path: 'test/implementation-status',
          element: <ImplementationStatus />,
        },
        {
          path: 'test/tab-state',
          element: <TabStateTest />,
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  }
);

const App: React.FC = () => {
  // 应用启动时清理过期的分页状态
  useEffect(() => {
    cleanupExpiredPageStates();
  }, []);

  return <RouterProvider router={router} />;
};

export default App; 